/**
 * Arrow-drawing modes for the drill canvas. Mirrors DrillArrow.kind /
 * style so a chip selection maps 1:1 to the arrow that gets stored.
 *
 * Web1 had Pass / Run / Dribble / Ball path on the left rail; we reuse
 * the same semantic kinds + visual conventions.
 */

import type { DrillArrow } from '@4cm/core/drills/types';

export type DrawingMode = 'erase' | DrillArrow['kind'];

export interface DrawingToolDef {
  id: Exclude<DrawingMode, undefined>;
  label: string;
  glyph: string;
  color: string;
  /** Default DrillArrow.style for this kind. */
  style: NonNullable<DrillArrow['style']>;
  /** Whether the arrow's path renders curved by default. */
  curvature: number;
  /** Tooltip describing the coaching distinction. Surfaced on hover. */
  description: string;
}

export const DRAWING_TOOLS: DrawingToolDef[] = [
  { id: 'pass',    label: 'Pass',     glyph: '→',  color: '#27AE60', style: 'solid',  curvature: 0,    description: 'Direct ball pass between players. Drag from passer to receiver.' },
  { id: 'run',     label: 'Run',      glyph: '⇢',  color: '#F39C12', style: 'dashed', curvature: 0,    description: 'Off-ball movement or supporting run. Drag from start to end of the run.' },
  { id: 'dribble', label: 'Dribble',  glyph: '∿',  color: '#E67E22', style: 'solid',  curvature: 0.35, description: 'Ball-carrier advances with the ball. Curved path shows the dribble line.' },
  { id: 'press',   label: 'Press',    glyph: '⤳',  color: '#C0392B', style: 'dashed', curvature: 0.15, description: 'Defensive pressure on the ball. Drag from defender to the pressing target.' },
  { id: 'cover',   label: 'Cover',    glyph: '↻',  color: '#2980B9', style: 'dotted', curvature: 0.2,  description: 'Defensive cover, screen, or recovery shape. Drag the covering line.' },
  { id: 'erase',   label: 'Erase',    glyph: '×',  color: '#95A5A6', style: 'solid',  curvature: 0,    description: 'Click any existing arrow to remove it.' },
];

/** Find a tool by mode id; returns null for null/undefined. */
export function toolForMode(mode: DrawingMode | null | undefined): DrawingToolDef | null {
  if (!mode) return null;
  return DRAWING_TOOLS.find((t) => t.id === mode) ?? null;
}
